import { Link } from "react-router-dom";
import { BadgeCheck, ShieldCheck, Wallet, Undo2, Headset, Users, ArrowRight } from "lucide-react";
import { LabPricingSlider } from "@/components/LabPricingSlider";
import { BookDemoButton } from "@/components/BookDemo";

const GUARANTEES = [
    { icon: BadgeCheck, t: "No hidden fees", d: "The slider price is the whole price — hosting, updates and your white-label subdomain included." },
    { icon: ShieldCheck, t: "Low risk", d: "Monthly plans cancel any time before renewal. No lock-in contracts." },
    { icon: Wallet, t: "No upfront payments", d: "No setup fees. Onboarding and data import are on us." },
    { icon: Undo2, t: "3-month money-back guarantee", d: "If Toothmatrix doesn't deliver for your lab, you leave with a full refund." },
    { icon: Headset, t: "Premium 1-on-1 support", d: "A direct line to our team — free of charge, in English or Arabic." },
    { icon: Users, t: "Unlimited licenses included", d: "Every technician, driver and accountant — plus free practice portals for all your clinics." },
];

export default function PricingLabs() {
    return (
        <div className="bg-slate-50 min-h-screen text-slate-900" data-testid="pricing-labs-page">
            {/* HERO + SPLIT */}
            <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1120] via-[#0e2e3a] to-[#0B1120] text-white">
                <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_25%_15%,rgba(14,165,233,0.18),transparent_50%),radial-gradient(circle_at_85%_85%,rgba(56,189,248,0.12),transparent_55%)]" />
                <div className="relative max-w-6xl mx-auto px-6 sm:px-8 pt-16 pb-20">
                    <div className="font-mono-tr text-[11px] tracking-[0.28em] uppercase text-cyan-300 mb-4">
                        Toothmatrix Lab · Pricing
                    </div>
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        {/* What you get */}
                        <div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display tracking-tight leading-[1.05]">
                                Pay for what<br />you <em className="not-italic text-cyan-400">produce.</em>
                            </h1>
                            <p className="mt-5 text-[15.5px] text-slate-300/95 max-w-md leading-relaxed">
                                Usage-based pricing in USD — pick your monthly case volume and that&rsquo;s
                                your price. Every feature included on every tier.
                            </p>
                            <h2 className="mt-10 mb-5 text-lg md:text-lg font-semibold text-white">What you get</h2>
                            <div className="grid sm:grid-cols-2 gap-4" data-testid="pl-guarantees">
                                {GUARANTEES.map((g) => (
                                    <div key={g.t} className="rounded-2xl bg-white/[0.06] border border-white/10 p-5 backdrop-blur-sm" data-testid={`pl-guarantee-${g.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                        <div className="flex items-center gap-2.5 mb-2">
                                            <g.icon size={16} className="text-emerald-400 shrink-0" />
                                            <div className="text-[14px] font-semibold text-white">{g.t}</div>
                                        </div>
                                        <p className="text-[12.5px] text-slate-300/90 leading-relaxed">{g.d}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Pricing card */}
                        <div className="lg:sticky lg:top-24">
                            <LabPricingSlider />
                        </div>
                    </div>
                </div>
            </section>

            {/* EVERY TIER INCLUDES */}
            <section className="max-w-6xl mx-auto px-6 sm:px-8 py-16">
                <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-700 mb-3">· Every tier includes</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        "Live dashboard, Kanban + WIP limits",
                        "Scan Inbox for every clinic scanner",
                        "Invoices, prepaid credits & multi-currency finance",
                        "Payroll & doctor commissions",
                        "White-label branding on your subdomain",
                        "Free practice portals for your clinics",
                        "Daily backups & tenant data isolation",
                        "Priority email + 1-on-1 support",
                    ].map((f) => (
                        <div key={f} className="rounded-xl bg-white border border-slate-200 px-4 py-3.5 text-[13.5px] text-slate-700">{f}</div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0B1120] text-white py-16">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
                    <h2 className="text-lg md:text-lg font-semibold sm:text-3xl sm:font-light font-display tracking-tight">See it on your own workflows first.</h2>
                    <p className="mt-4 text-slate-300/90 leading-relaxed">
                        Book a 30-minute demo — we&rsquo;ll walk through your lab&rsquo;s real cases, not a canned deck.
                    </p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                        <BookDemoButton testid="pl-cta-book-demo"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-400 text-[#0B1120] font-medium text-[14px] hover:bg-cyan-300 transition" />
                        <Link to="/solutions/dental-labs" data-testid="pl-cta-explore"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/20 hover:border-cyan-300 text-white/90 font-medium text-[14px] transition">
                            Explore the product <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
